import { Divider, Input, makeStyles, Subtitle2, Switch, tokens } from "@fluentui/react-components"
import { DOMAIN_SLUG_REGEX, type ProblemDetails, USERNAME_REGEX } from "@rema/shared"
import type { CreateDomainRequest, Domain, UpdateDomainRequest } from "@rema/shared/schemas/domains"
import { useEffect } from "react"
import { Controller, useForm } from "react-hook-form"
import { AppDrawerForm } from "../../components/app-drawer-form"
import { FormField } from "../../components/form-field"
import { ApiError } from "../../lib/api"
import { applyFieldErrors, problemMessage } from "../../lib/problem"
import { domainsI18n as i18n } from "./i18n"

const useStyles = makeStyles({
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  sectionTitle: {
    marginBottom: tokens.spacingVerticalXS,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
})

// Formulario único de alta/edición. En alta pide el dominio + su maestro inicial;
// en edición permite nombre, política de registro y cuotas (el slug y el maestro no
// se editan aquí). Validación inline por campo (docs/05 §3) + mapeo de errors[].
interface DomainFormValues {
  slug: string
  name: string
  masterUsername: string
  masterName: string
  masterEmail: string
  masterPassword: string
  regEnabled: boolean
  regAutoLogin: boolean
  regRequireEmail: boolean
  regRequireVerification: boolean
  regDefaultRoles: string
  quotaMaxUsers: string
  quotaMaxClients: string
  pinToNode: boolean
}

function defaultsFor(domain: Domain | null): DomainFormValues {
  return {
    slug: domain?.slug ?? "",
    name: domain?.name ?? "",
    masterUsername: "",
    masterName: "",
    masterEmail: "",
    masterPassword: "",
    regEnabled: domain?.policies.registration.enabled ?? false,
    regAutoLogin: domain?.policies.registration.auto_login ?? true,
    regRequireEmail: domain?.policies.registration.require_email ?? false,
    regRequireVerification: domain?.policies.registration.require_verification ?? false,
    regDefaultRoles: (domain?.policies.registration.default_roles ?? ["user"]).join(", "),
    quotaMaxUsers: domain?.quotas.max_users != null ? String(domain.quotas.max_users) : "",
    quotaMaxClients: domain?.quotas.max_clients != null ? String(domain.quotas.max_clients) : "",
    // Alta: activado por defecto (anclaje por dominio). Edición: refleja el estado real.
    pinToNode: domain ? domain.node_id != null : true,
  }
}

// Cuota: cadena vacía ⇒ null (sin límite); si no, entero no negativo.
function parseQuota(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === "") return null
  return Number(trimmed)
}

function parseRoles(raw: string): string[] {
  return raw
    .split(",")
    .map((role) => role.trim())
    .filter((role) => role.length > 0)
}

export interface DomainFormDrawerProps {
  open: boolean
  // null = alta; un dominio = edición
  domain: Domain | null
  onClose: () => void
  onCreate: (input: CreateDomainRequest) => Promise<unknown>
  onUpdate: (id: string, input: UpdateDomainRequest) => Promise<unknown>
}

export const DomainFormDrawer = ({
  open,
  domain,
  onClose,
  onCreate,
  onUpdate,
}: DomainFormDrawerProps) => {
  const styles = useStyles()
  const isEdit = domain !== null
  const { control, handleSubmit, reset, setError, formState } = useForm<DomainFormValues>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: defaultsFor(domain),
  })

  // Cada apertura recarga los valores del dominio en edición (o limpia en alta).
  useEffect(() => {
    if (open) reset(defaultsFor(domain))
  }, [open, domain, reset])

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (isEdit) {
        const update: UpdateDomainRequest = {
          name: values.name.trim(),
          quotas: {
            max_users: parseQuota(values.quotaMaxUsers),
            max_clients: parseQuota(values.quotaMaxClients),
          },
          policies: {
            registration: {
              enabled: values.regEnabled,
              auto_login: values.regAutoLogin,
              require_email: values.regRequireEmail,
              require_verification: values.regRequireVerification,
              default_roles: parseRoles(values.regDefaultRoles),
            },
          },
        }
        // Solo se envía el anclaje si cambió, para que guardar otros campos no
        // re-ancle (re-hashee) el dominio sin querer.
        if (values.pinToNode !== (domain.node_id != null)) {
          update.pin_to_node = values.pinToNode
        }
        await onUpdate(domain.id, update)
      } else {
        const create: CreateDomainRequest = {
          slug: values.slug.trim().toLowerCase(),
          name: values.name.trim(),
          master: {
            username: values.masterUsername.trim().toLowerCase(),
            password: values.masterPassword,
            ...(values.masterName.trim() ? { name: values.masterName.trim() } : {}),
            ...(values.masterEmail.trim() ? { email: values.masterEmail.trim() } : {}),
          },
          quotas: {
            max_users: parseQuota(values.quotaMaxUsers),
            max_clients: parseQuota(values.quotaMaxClients),
          },
          policies: {
            registration: {
              enabled: values.regEnabled,
              auto_login: values.regAutoLogin,
              require_email: values.regRequireEmail,
              require_verification: values.regRequireVerification,
              default_roles: parseRoles(values.regDefaultRoles),
            },
          },
          pin_to_node: values.pinToNode,
        }
        await onCreate(create)
      }
      onClose()
    } catch (error) {
      if (error instanceof ApiError) {
        // errors[] → cada campo en rojo (el backend usa "master.password", etc.);
        // el resto (conflict de slug…) va al MessageBar del formulario.
        const mapped = applyFieldErrors(remapServerFields(error.problem), setError)
        if (!mapped) setFormError(problemMessage(error.problem))
      } else {
        throw error
      }
    }
  })

  // El servidor responde un solo error de formulario no ligado a campo (slug duplicado).
  const formError = formState.errors.root?.message ?? null
  const setFormError = (message: string) => setError("root", { message })

  return (
    <AppDrawerForm
      open={open}
      title={isEdit ? i18n.editDrawer.title : i18n.createDrawer.title}
      submitLabel={isEdit ? i18n.editDrawer.submit : i18n.createDrawer.submit}
      submitting={formState.isSubmitting}
      error={formError}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      {!isEdit ? (
        <div className={styles.section}>
          <Subtitle2 className={styles.sectionTitle}>{i18n.createDrawer.sectionDomain}</Subtitle2>
          <FormField
            control={control}
            name="slug"
            label={i18n.fields.slug}
            required
            hint={i18n.fields.slugHint}
            rules={{
              required: i18n.validation.slugRequired,
              validate: (value) =>
                DOMAIN_SLUG_REGEX.test(String(value).trim().toLowerCase()) ||
                i18n.validation.slugFormat,
            }}
          >
            {(field, invalid) => <Input {...field} placeholder="acme" aria-invalid={invalid} />}
          </FormField>
          <FormField
            control={control}
            name="name"
            label={i18n.fields.name}
            required
            rules={{ required: i18n.validation.nameRequired }}
          >
            {(field, invalid) => <Input {...field} aria-invalid={invalid} />}
          </FormField>
        </div>
      ) : (
        <div className={styles.section}>
          <Subtitle2 className={styles.sectionTitle}>{i18n.editDrawer.sectionGeneral}</Subtitle2>
          <FormField
            control={control}
            name="name"
            label={i18n.fields.name}
            required
            rules={{ required: i18n.validation.nameRequired }}
          >
            {(field, invalid) => <Input {...field} aria-invalid={invalid} />}
          </FormField>
        </div>
      )}

      {!isEdit ? (
        <>
          <Divider />
          <div className={styles.section}>
            <Subtitle2 className={styles.sectionTitle}>{i18n.createDrawer.sectionMaster}</Subtitle2>
            <FormField
              control={control}
              name="masterUsername"
              label={i18n.fields.masterUsername}
              required
              hint={i18n.fields.masterUsernameHint}
              rules={{
                required: i18n.validation.masterUsernameRequired,
                validate: (value) =>
                  USERNAME_REGEX.test(String(value).trim().toLowerCase()) ||
                  i18n.validation.masterUsernameFormat,
              }}
            >
              {(field, invalid) => <Input {...field} aria-invalid={invalid} />}
            </FormField>
            <FormField control={control} name="masterName" label={i18n.fields.masterName}>
              {(field) => <Input {...field} />}
            </FormField>
            <FormField
              control={control}
              name="masterEmail"
              label={i18n.fields.masterEmail}
              rules={{
                validate: (value) =>
                  String(value).trim() === "" ||
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim()) ||
                  i18n.validation.emailFormat,
              }}
            >
              {(field, invalid) => <Input {...field} type="email" aria-invalid={invalid} />}
            </FormField>
            <FormField
              control={control}
              name="masterPassword"
              label={i18n.fields.masterPassword}
              required
              rules={{ required: i18n.validation.masterPasswordRequired }}
            >
              {(field, invalid) => (
                <Input
                  {...field}
                  type="password"
                  autoComplete="new-password"
                  aria-invalid={invalid}
                />
              )}
            </FormField>
          </div>
        </>
      ) : null}

      <Divider />
      <div className={styles.section}>
        <Subtitle2 className={styles.sectionTitle}>{i18n.editDrawer.sectionRegistration}</Subtitle2>
        <Controller
          control={control}
          name="regEnabled"
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(_, data) => field.onChange(data.checked)}
              label={i18n.fields.regEnabled}
            />
          )}
        />
        <Controller
          control={control}
          name="regAutoLogin"
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(_, data) => field.onChange(data.checked)}
              label={i18n.fields.regAutoLogin}
            />
          )}
        />
        <Controller
          control={control}
          name="regRequireEmail"
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(_, data) => field.onChange(data.checked)}
              label={i18n.fields.regRequireEmail}
            />
          )}
        />
        <Controller
          control={control}
          name="regRequireVerification"
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(_, data) => field.onChange(data.checked)}
              label={i18n.fields.regRequireVerification}
            />
          )}
        />
        <FormField control={control} name="regDefaultRoles" label={i18n.fields.regDefaultRoles}>
          {(field) => <Input {...field} placeholder="user" />}
        </FormField>
      </div>

      <Divider />
      <div className={styles.section}>
        <Subtitle2 className={styles.sectionTitle}>{i18n.editDrawer.sectionQuotas}</Subtitle2>
        <FormField
          control={control}
          name="quotaMaxUsers"
          label={i18n.fields.quotaMaxUsers}
          hint={i18n.fields.quotaUnlimited}
          rules={{
            validate: (value) =>
              isNonNegativeOrEmpty(String(value)) || i18n.validation.quotaNonNegative,
          }}
        >
          {(field, invalid) => <Input {...field} type="number" min={0} aria-invalid={invalid} />}
        </FormField>
        <FormField
          control={control}
          name="quotaMaxClients"
          label={i18n.fields.quotaMaxClients}
          hint={i18n.fields.quotaUnlimited}
          rules={{
            validate: (value) =>
              isNonNegativeOrEmpty(String(value)) || i18n.validation.quotaNonNegative,
          }}
        >
          {(field, invalid) => <Input {...field} type="number" min={0} aria-invalid={invalid} />}
        </FormField>
      </div>

      <Divider />
      <div className={styles.section}>
        <Subtitle2 className={styles.sectionTitle}>{i18n.editDrawer.sectionNode}</Subtitle2>
        <Controller
          control={control}
          name="pinToNode"
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(_, data) => field.onChange(data.checked)}
              label={i18n.fields.pinToNode}
            />
          )}
        />
        <span className={styles.hint}>{i18n.fields.pinToNodeHint}</span>
      </div>
    </AppDrawerForm>
  )
}

function isNonNegativeOrEmpty(raw: string): boolean {
  const trimmed = raw.trim()
  if (trimmed === "") return true
  const value = Number(trimmed)
  return Number.isInteger(value) && value >= 0
}

// El backend marca campos del maestro como "master.password"; react-hook-form usa el
// nombre del campo del formulario, así que se mapean los paths del contrato a los inputs.
const SERVER_FIELD_MAP: Record<string, string> = {
  "master.password": "masterPassword",
  "master.username": "masterUsername",
  "master.email": "masterEmail",
  "master.name": "masterName",
  slug: "slug",
  name: "name",
}

function remapServerFields(problem: ProblemDetails): ProblemDetails {
  if (!problem.errors) return problem
  return {
    ...problem,
    errors: problem.errors.map((fieldError) => ({
      ...fieldError,
      field: SERVER_FIELD_MAP[fieldError.field] ?? fieldError.field,
    })),
  }
}
